import { useEffect, useCallback, useRef } from "react";
import { useSocket } from "../context/SocketContext";

export const useAuctionSocket = (options = {}) => {
    const { socketService, isConnected, socket } = useSocket();

    // Store callbacks in refs to prevent unnecessary re-registrations
    const callbacksRef = useRef(options);

    // Update callbacks ref when options change
    useEffect(() => {
        callbacksRef.current = options;
    }, [options]);

    // Register event listeners with stable handlers
    useEffect(() => {
        if (!socket || !isConnected) {
            console.log("[useAuctionSocket] Socket not available or not connected, skipping listener registration");
            return;
        }

        console.log("[useAuctionSocket] Registering listeners for socket ID:", socket.id);

        const listeners = [
            ["joined", () => callbacksRef.current.onJoined],
            ["bidding_started", () => callbacksRef.current.onBiddingStarted],
            ["new_highest_bid", () => callbacksRef.current.onNewHighestBid],
            ["end_time_extended", () => callbacksRef.current.onEndTimeExtended],
            ["bidding_ended", () => callbacksRef.current.onBiddingEnded],
            ["participant_joined", () => callbacksRef.current.onParticipantJoined],
            ["participant_left", () => callbacksRef.current.onParticipantLeft],
            ["bid_placed", () => callbacksRef.current.onBidPlaced],
            ["bid_rejected", () => callbacksRef.current.onBidRejected],
            ["error", () => callbacksRef.current.onError],
        ];

        const registeredHandlers = [];

        listeners.forEach(([event, getCallback]) => {
            const handler = (data) => {
                const callback = getCallback();
                if (typeof callback === "function") {
                    console.log(`[Socket EVENT] ${event}:`, data);
                    callback(data);
                } else {
                    console.warn(`[Socket] No callback for: ${event}`);
                }
            };

            socket.on(event, handler);
            registeredHandlers.push({ event, handler });
        });

        return () => {
            console.log("[useAuctionSocket] Cleaning up listeners for socket ID:", socket.id);
            registeredHandlers.forEach(({ event, handler }) => {
                socket.off(event, handler);
            });
        };
    }, [socket, isConnected]); // Re-register if socket or connection status changes

    const joinBiddingRoom = useCallback((sessionId) => {
        if (!socketService) {
            console.warn("[useAuctionSocket] Cannot join room: socketService not available");
            return false;
        }

        // Check if already joined using SocketHandler's registry
        if (socketService.isRoomJoined(sessionId)) {
            console.log(`[useAuctionSocket] Already joined room ${sessionId}, re-joining to sync state...`);
            // Still call service to ensure backend room state and get 'joined' state event
        }

        if (!isConnected) {
            console.warn("[useAuctionSocket] Cannot join room: socket not connected");
            return false;
        }

        console.log(`[useAuctionSocket] Joining room: ${sessionId}`);
        const result = socketService.joinBiddingRoom(sessionId);
        return result;
    }, [socketService, isConnected]);

    const leaveBiddingRoom = useCallback((sessionId) => {
        if (!socketService) {
            console.warn("[useAuctionSocket] Cannot leave room: socketService not available");
            return false;
        }

        // Check if already left using SocketHandler's registry
        if (!socketService.isRoomJoined(sessionId)) {
            console.log(`[useAuctionSocket] Already left room ${sessionId}`);
            return true; // already left
        }

        console.log(`[useAuctionSocket] Leaving room: ${sessionId}`);
        const result = socketService.leaveBiddingRoom(sessionId);
        return result;
    }, [socketService]);

    const placeBid = useCallback((sessionId, amount) => {
        if (!socketService) return false;
        return socketService.placeBid(sessionId, amount);
    }, [socketService]);

    const getHandler = useCallback(() => socketService, [socketService]);

    return {
        isConnected,
        joinBiddingRoom,
        leaveBiddingRoom,
        placeBid,
        getHandler,
        socket,
    };
};
